import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileText, Download, Clock, Lock, CheckCircle2, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import DownloadDisclaimerModal from "../components/DownloadDisclaimerModal";

/**
 * Pagina PUBBLICA per il download di asset fornitori condivisi via link 48h.
 * Il cliente esterno si identifica (nome + email), accetta il disclaimer
 * (firmato nel registro audit) e può scaricare i file finché il link è valido.
 *
 * Path: /asset-share/:token
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  catalogo: "Catalogo", listino: "Listino", finiture: "Finiture",
  cad_3d: "File CAD/3D", comunicazioni: "Comunicazioni", altro: "Altro",
};

function fmtSize(bytes) {
  const n = Number(bytes) || 0;
  if (n <= 0) return "";
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function hoursLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60)));
}

export default function SharedAssets() {
  const { token } = useParams();
  const storageKey = `ah_ashare_${token}`;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [formError, setFormError] = useState("");
  const [audit, setAudit] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "null"); } catch { return null; }
  });
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/asset-share/${token}`);
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || "Link non valido");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const doAccept = useCallback(async () => {
    try {
      const { data: r } = await axios.post(`${API}/asset-share/${token}/accept`, {
        client_name: clientName.trim(),
        client_email: clientEmail.trim(),
      });
      const rec = { audit_id: r.audit_id, name: clientName.trim(), email: clientEmail.trim() };
      localStorage.setItem(storageKey, JSON.stringify(rec));
      setAudit(rec);
    } catch (e) {
      setFormError(e.response?.data?.detail || "Errore registrazione accettazione. Riprova.");
      throw e;
    }
  }, [token, clientName, clientEmail, storageKey]);

  function openDisclaimer() {
    setFormError("");
    const name = clientName.trim();
    const email = clientEmail.trim();
    if (!name) { setFormError("Inserisci il tuo nome e cognome."); return; }
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) { setFormError("Inserisci un indirizzo email valido."); return; }
    setModalOpen(true);
  }

  function downloadUrl(fileId) {
    return `${API}/asset-share/${token}/download/${fileId}?audit_id=${encodeURIComponent(audit?.audit_id || "")}`;
  }

  const hrs = data ? hoursLeft(data.expires_at) : 0;

  return (
    <div className="min-h-screen bg-cream text-ink flex flex-col">
      {/* Header minimale */}
      <header className="border-b border-ink/10 bg-white">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <div className="font-display text-lg tracking-[0.25em] uppercase">ARCHI-HUB</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 inline-flex items-center gap-1.5">
            <ShieldCheck size={12} className="text-gold" /> Condivisione riservata
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-5 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate1" data-testid="shared-assets-loading">
            <Loader2 size={22} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-white border border-rose-200 rounded-sm p-8 text-center space-y-3" data-testid="shared-assets-error">
            <AlertTriangle size={28} className="mx-auto text-rose-600" />
            <div className="font-display text-xl">Link non disponibile</div>
            <p className="text-sm text-slate1">{error}</p>
            <p className="text-xs text-slate1">Contatta chi ti ha inviato il link per ricevere una nuova condivisione.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="shared-assets-page">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-slate1 mb-1">Documenti condivisi</div>
              <h1 className="font-display text-2xl md:text-3xl font-light">{data.name}</h1>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-gold/10 border border-gold/40 rounded-sm text-[11px] tracking-[0.1em] uppercase text-ink" data-testid="shared-assets-expiry">
                <Clock size={12} className="text-gold" />
                Link valido ancora {hrs} or{hrs === 1 ? "a" : "e"}
              </div>
            </div>

            {/* Step identificazione + accettazione */}
            {!audit ? (
              <div className="bg-white border border-ink/10 rounded-sm p-5 space-y-4" data-testid="shared-assets-identify">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 shrink-0 rounded-sm bg-ink text-cream flex items-center justify-center">
                    <Lock size={16} />
                  </div>
                  <div>
                    <div className="text-sm font-medium">Identificati per sbloccare i download</div>
                    <p className="text-xs text-slate1 mt-0.5">
                      Prima di scaricare i documenti è necessario indicare nome ed email e accettare le
                      Condizioni di Utilizzo. L&apos;accettazione viene registrata con firma digitale.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Nome e cognome *"
                    className="px-3 py-2.5 text-sm bg-cream border border-ink/15 rounded-sm focus:outline-none focus:border-ink"
                    data-testid="shared-assets-name-input"
                  />
                  <input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="Email *"
                    className="px-3 py-2.5 text-sm bg-cream border border-ink/15 rounded-sm focus:outline-none focus:border-ink"
                    data-testid="shared-assets-email-input"
                  />
                </div>
                {formError && (
                  <div className="text-xs text-rose-600" data-testid="shared-assets-form-error">{formError}</div>
                )}
                <button
                  onClick={openDisclaimer}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 transition-colors"
                  data-testid="shared-assets-unlock-btn"
                >
                  <ShieldCheck size={13} /> Continua e accetta le condizioni
                </button>
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-300 rounded-sm px-4 py-3 flex items-center gap-2 text-sm text-emerald-800" data-testid="shared-assets-accepted-banner">
                <CheckCircle2 size={16} className="shrink-0" />
                Condizioni accettate da <strong>{audit.name}</strong> ({audit.email}) — puoi scaricare i file.
              </div>
            )}

            {/* Lista file */}
            <div className="space-y-2" data-testid="shared-assets-files">
              {(data.files || []).map((f) => (
                <div key={f.id} className="bg-white border border-ink/10 rounded-sm p-4 flex items-center gap-3" data-testid={`shared-asset-${f.id}`}>
                  <div className="w-9 h-9 shrink-0 rounded-sm bg-cream2 text-slate1 flex items-center justify-center">
                    <FileText size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate" title={f.title || f.filename}>{f.title || f.filename}</div>
                    <div className="text-[11px] text-slate1 flex items-center gap-2 flex-wrap mt-0.5">
                      {f.supplier_name && <span>{f.supplier_name}</span>}
                      {f.kind && <span className="uppercase tracking-[0.1em]">{KIND_LABEL[f.kind] || f.kind}</span>}
                      {f.size > 0 && <span>{fmtSize(f.size)}</span>}
                    </div>
                  </div>
                  {audit ? (
                    <a
                      href={downloadUrl(f.id)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-ink text-cream text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/90 transition-colors shrink-0"
                      data-testid={`shared-asset-download-${f.id}`}
                    >
                      <Download size={12} /> Scarica
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3.5 py-2 border border-ink/15 text-slate1 text-[10px] tracking-[0.2em] uppercase rounded-sm shrink-0" data-testid={`shared-asset-locked-${f.id}`}>
                      <Lock size={12} /> Bloccato
                    </span>
                  )}
                </div>
              ))}
            </div>

            <p className="text-[11px] text-slate1 leading-relaxed">
              Materiale strettamente riservato, fornito esclusivamente a scopo consultativo.
              È vietata qualsiasi forma di riproduzione, distribuzione o divulgazione a terzi.
            </p>
          </div>
        )}
      </main>

      <footer className="border-t border-ink/10 bg-white py-4">
        <div className="max-w-3xl mx-auto px-5 text-[10px] tracking-[0.2em] uppercase text-slate1 text-center">
          ARCHI-HUB · Documenti riservati · Link temporaneo 48h
        </div>
      </footer>

      <DownloadDisclaimerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAccept={() => setModalOpen(false)}
        fileLabel={data ? `${(data.files || []).length} document${(data.files || []).length === 1 ? "o condiviso" : "i condivisi"} · ${data.name}` : ""}
        customAccept={doAccept}
      />
    </div>
  );
}
